import { useUnlockAssessment } from '@apps/materiality-tracker/hooks/useUnlockAssessment';
import { ROUTES } from '@constants/routes';
import { Blueprints } from '@features/survey-configuration/types';
import { DashboardDivider } from '@g17eco/atoms/divider';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { BasicAlert } from '@g17eco/molecules/alert';
import Dashboard, { DashboardRow, DashboardSection } from '@g17eco/molecules/dashboard';
import { generateErrorToast } from '@g17eco/molecules/toasts';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { useToggle } from '@hooks/useToggle';
import { generateUrl } from '@routes/util';
import { DataPeriods } from '@utils/dataPeriods';
import { getEffectiveDate, hasSurveyConflict } from '@utils/survey';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Col, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { useCreateSurveyMutation, useGetSurveysQuery } from '../../api/materiality-assessment';
import { useCreateAssessmentForm } from '../../hooks/useCreateAssessmentForm';
import { AssessmentConfigurationForm } from '../../components/assessment-create/AssessmentConfigurationForm';

enum Action {
  RequestInvoice = 'request-invoice',
  PayWithStripe = 'pay-with-stripe',
}

export const AssessmentCreateRoute = () => {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const history = useHistory();
  const [nextStepModal, toggleNextStepModal, openNextStepModal] = useToggle();
  const { data: surveys = [], isFetching: isGettingSurveys } = useGetSurveysQuery({ initiativeId });
  const [createSurvey, { isLoading: isCreating }] = useCreateSurveyMutation();
  const { handleUnlock } = useUnlockAssessment();

  const {
    assessmentForm,
    isAllUtrsAnswered,
    isLoading: isGettingUtrs,
    utrs,
    updateAssessmentForm,
    handleOptionChange,
  } = useCreateAssessmentForm({
    initiativeId,
  });

  const { month, year, answers, assessmentType, verificationRequired } = assessmentForm;

  const isConflict = hasSurveyConflict(
    surveys,
    {
      initiativeId,
      year,
      month: month ? month - 1 : month,
      type: SurveyType.Materiality,
      period: DataPeriods.Yearly,
      sourceName: Blueprints.Materiality2024,
    },
    undefined,
  );

  const isFormValid = isAllUtrsAnswered && month && year && !isConflict && assessmentType;

  const handleCreate = ({ action }: { action: Action }) => {
    if (!isFormValid || !answers) {
      return;
    }
    toggleNextStepModal();
    createSurvey({
      initiativeId,
      effectiveDate: getEffectiveDate(month, year),
      answers,
      assessmentType,
      verificationRequired,
      requestInvoice: action === Action.RequestInvoice,
    })
      .unwrap()
      .then((response) => {
        const returnUrl = generateUrl(ROUTES.MATERIALITY_TRACKER_SURVEY, {
          initiativeId,
          surveyId: response._id,
        });
        if (action === Action.PayWithStripe) {
          handleUnlock(response, returnUrl);
          return;
        }
        history.push(returnUrl);
      })
      .catch((e) => generateErrorToast(e));
  };

  const goToList = () => {
    history.push(generateUrl(ROUTES.MATERIALITY_TRACKER_LIST, { initiativeId }));
  };

  const isLoading = isCreating || isGettingSurveys || isGettingUtrs;

  return (
    <Dashboard>
      <DashboardRow>
        <Button color='link' className='px-2 pt-2' onClick={goToList}>
          <i className='fal fa-caret-left mr-2' />
          Go back to all assessments page
        </Button>
      </DashboardRow>
      <DashboardSection>
        {isLoading ? <BlockingLoader /> : null}
        <h4>Create a new assessment</h4>

        <BasicAlert hide={!isConflict} type={'warning'}>
          <b>WARNING:</b> An assessment already exists for the selected date.
        </BasicAlert>
        <DashboardDivider className='my-4 border-ThemeBorderDefault' />
        <AssessmentConfigurationForm
          verificationRequired={verificationRequired}
          assessmentType={assessmentType}
          utrs={utrs}
          updateAssessmentForm={updateAssessmentForm}
          handleOptionChange={handleOptionChange}
        />
        <div className='mt-3'>
          <div className='text-right pt-4'>
            <Button color='link-secondary' onClick={goToList} className='me-3'>
              Cancel
            </Button>
            <Button
              color='primary'
              size='xl'
              disabled={!isFormValid || isLoading}
              onClick={() => openNextStepModal(true)}
            >
              Generate assessment
            </Button>
          </div>
        </div>
      </DashboardSection>
      <Modal isOpen={nextStepModal} toggle={toggleNextStepModal} backdrop='static'>
        <ModalHeader toggle={toggleNextStepModal}>Ready to start your next assessment?</ModalHeader>
        <ModalBody className='text-ThemeTextMedium'>
          Before generating your assessment, please choose your preferred payment method.
          <Row className='mt-3'>
            <Col>
              <Button
                size='xl'
                color='secondary'
                onClick={() => handleCreate({ action: Action.RequestInvoice })}
                className='w-100'
              >
                Request invoice
              </Button>
              <div className='text-sm text-center'>We'll be in touch within 1 business day</div>
            </Col>
            <Col>
              <Button
                size='xl'
                color='primary'
                onClick={() => handleCreate({ action: Action.PayWithStripe })}
                className='w-100'
              >
                Pay now with stripe
              </Button>
              <div className='text-sm text-center'>Immediate access</div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </Dashboard>
  );
};
